import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { ShieldCheck, Warning, Package, Lock, CheckCircle, Copy, ArrowLeft, MapPin, Cube } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = "/assets/logo.png";

function scoreColor(score) {
  if (score >= 80) return "#16a34a";
  if (score >= 50) return "#d97706";
  if (score >= 25) return "#ea580c";
  return "#dc2626";
}

const EVENT_META = {
  delivery: { icon: CheckCircle, color: "#16a34a", label: "Entrega concluida" },
  tamper: { icon: Warning, color: "#dc2626", label: "Tentativa de abertura" },
  quarantine: { icon: Lock, color: "#7c3aed", label: "Quarentena" },
};

function StatCard({ icon: Icon, value, label, color, danger }) {
  return (
    <div style={{ background: "var(--surface)", borderRadius: 14, padding: "16px 14px", border: "1px solid var(--border-color)" }}>
      <Icon size={18} weight="duotone" style={{ color }} />
      <div style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", marginTop: 6, color: danger ? color : "var(--text-main)" }}>{value}</div>
      <div style={{ fontSize: "0.66rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</div>
    </div>
  );
}

export default function BagReputation({ bagCode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/bags/${encodeURIComponent(bagCode)}/reputation`);
        if (active) setData(r.data);
      } catch (e) {
        if (active) setError(e?.response?.status === 404 ? "Bolsa nao encontrada." : "Nao foi possivel carregar a reputacao.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [bagCode]);

  useEffect(() => {
    if (!data) return;
    document.title = `Reputacao da bolsa ${data.bag_code} \u00b7 BluMove`;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = `Score de fiabilidade ${data.reliability_score}/100 \u00b7 ${data.delivered_ok} entregas \u00b7 ${data.tamper_attempts_lifetime} tentativas de abertura.`;
    setMeta("property", "og:title", `Bolsa ${data.bag_code} \u00b7 Reputacao BluMove`);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", LOGO);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", typeof window !== "undefined" ? window.location.href : "");
    setMeta("name", "twitter:card", "summary");
    setMeta("name", "description", desc);
  }, [data]);

  const score = data?.reliability_score ?? 0;
  const color = useMemo(() => scoreColor(score), [score]);
  const shareUrl = typeof window !== "undefined" ? window.location.href : `/bag/${bagCode}`;

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Bolsa ${data?.bag_code} \u00b7 BluMove`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (e) { /* cancelled */ }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }} data-testid="bag-reputation-page">
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "28px 18px 60px" }}>
        <a href="/" className="flex items-center gap-2" style={{ textDecoration: "none", color: "var(--text-muted)", fontSize: "0.82rem", fontWeight: 600, marginBottom: 20 }}>
          <ArrowLeft size={16} /> <img src={LOGO} alt="BluMove" style={{ height: 22 }} />
        </a>

        {loading ? (
          <p style={{ textAlign: "center", color: "var(--text-muted)", padding: "60px 0" }}>A carregar reputacao...</p>
        ) : error ? (
          <div className="flex flex-col items-center" style={{ padding: "60px 20px", textAlign: "center" }}>
            <Warning size={40} weight="duotone" style={{ color: "#dc2626" }} />
            <p style={{ fontFamily: "Outfit", fontWeight: 600, marginTop: 12 }}>{error}</p>
            <span style={{ fontFamily: "JetBrains Mono", color: "var(--text-muted)", fontSize: "0.8rem", marginTop: 4 }}>{bagCode}</span>
          </div>
        ) : data ? (
          <>
            {/* Dark hero */}
            <div style={{ borderRadius: 22, padding: "28px 24px", background: "linear-gradient(135deg, #10151f 0%, #1b2433 100%)", color: "#fff", position: "relative", overflow: "hidden" }}>
              <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", color: "rgba(255,255,255,0.55)", display: "flex", alignItems: "center", gap: 6 }}>
                <Cube size={13} weight="duotone" /> REPUTACAO DE BOLSA
              </div>
              <div className="flex items-center gap-3" style={{ marginTop: 8 }}>
                <h1 style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "2rem", lineHeight: 1 }}>{data.bag_code}</h1>
                {data.quarantined && (
                  <span style={{ background: "rgba(124,58,237,0.25)", color: "#c4b5fd", fontSize: "0.66rem", fontWeight: 700, padding: "3px 9px", borderRadius: 9999 }}>EM QUARENTENA</span>
                )}
              </div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", marginTop: 4 }}>{data.material} · {data.format_label}</div>
              {data.current_location && (
                <div className="flex items-center gap-1" style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", marginTop: 6 }}>
                  <MapPin size={13} weight="fill" /> {data.current_location}
                </div>
              )}
              <div style={{ marginTop: 22 }}>
                <div style={{ fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)" }}>SCORE DE FIABILIDADE</div>
                <div className="flex items-end gap-2" style={{ marginTop: 2 }}>
                  <span style={{ fontFamily: "Outfit", fontWeight: 800, fontSize: "3.4rem", lineHeight: 1, color }}>{score}</span>
                  <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", marginBottom: 8 }}>/100</span>
                  <span data-testid="bag-status-label" style={{ marginLeft: 8, marginBottom: 12, background: color, color: "#fff", fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.06em", padding: "4px 10px", borderRadius: 9999 }}>{data.status_label}</span>
                </div>
              </div>
            </div>

            {/* Stats grid (6) */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" style={{ marginTop: 16 }}>
              <StatCard icon={Package} value={data.total_shipments} label="Envios totais" color="var(--primary)" />
              <StatCard icon={CheckCircle} value={data.delivered_ok} label="Entregas OK" color="#16a34a" />
              <StatCard icon={Cube} value={data.historical_deliveries} label="Entregas historicas" color="#0ea5e9" />
              <StatCard icon={Warning} value={data.tamper_attempts_lifetime} label="Tentativas totais" color="#dc2626" danger={data.tamper_attempts_lifetime > 0} />
              <StatCard icon={Warning} value={data.tamper_last_24h} label="Tentativas 24h" color="#dc2626" danger={data.tamper_last_24h > 0} />
              <StatCard icon={Lock} value={data.quarantine_count} label="Quarentenas" color="#7c3aed" danger={data.quarantine_count > 0} />
            </div>

            {/* Share */}
            <button onClick={share} data-testid="bag-share-btn" className="flex items-center justify-center gap-2" style={{ width: "100%", marginTop: 16, padding: "13px", borderRadius: 12, border: "none", background: "var(--primary)", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: "0.9rem" }}>
              <Copy size={16} weight="duotone" /> {copied ? "Link copiado!" : "Partilhar esta reputacao"}
            </button>

            {/* QR */}
            <div className="flex flex-col items-center" style={{ marginTop: 18, gap: 6 }} data-testid="bag-qr">
              <div style={{ background: "#fff", padding: 10, borderRadius: 12, border: "1px solid var(--border-color)" }}>
                <QRCodeSVG value={shareUrl} size={104} level="M" marginSize={0} />
              </div>
              <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>Aponte a camara para abrir esta pagina</span>
            </div>

            {/* Timeline */}
            <h3 className="flex items-center gap-2" style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1rem", margin: "26px 0 12px" }}>
              <ShieldCheck size={18} weight="duotone" style={{ color: "var(--primary)" }} /> Ultimos eventos
            </h3>
            {data.last_events && data.last_events.length > 0 ? (
              <div className="flex flex-col gap-2">
                {data.last_events.map((ev, i) => {
                  const meta = EVENT_META[ev.type] || EVENT_META.delivery;
                  const Icon = meta.icon;
                  const shock = ev.type === "tamper" && ev.shock_g ? ` \u00b7 ${Number(ev.shock_g).toFixed(1)}g` : "";
                  return (
                    <div key={`${ev.type}-${ev.at}-${i}`} className="flex items-center gap-3" style={{ background: "var(--surface)", borderRadius: 12, padding: "12px 14px", border: "1px solid var(--border-color)" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 10, background: `${meta.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon size={17} weight="duotone" style={{ color: meta.color }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: "0.85rem", color: meta.color }}>{meta.label}</div>
                        <div className="flex items-center gap-1" style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>
                          <MapPin size={11} weight="fill" /> {ev.location || "\u2014"}{shock}{ev.tracking_code ? ` \u00b7 ${ev.tracking_code}` : ""}
                        </div>
                      </div>
                      <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", flexShrink: 0 }}>
                        {ev.at ? new Date(ev.at).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }) : ""}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Sem eventos registados para esta bolsa.</p>
            )}

            <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 30 }}>
              Pagina publica de transparencia BluMove · dados atualizados em tempo real
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
